import { useState, useEffect } from 'react';
import { Bell, Calendar, Trophy, Zap, ShoppingCart, Cake, Info, Target, ChevronDown } from 'lucide-react';

const NOTIF_TYPE = {
  birthday: { Icon: Cake, color: '#FF00B8' },
  event: { Icon: Calendar, color: '#3b82f6' },
  challenge: { Icon: Trophy, color: '#FFB800' },
  defi: { Icon: Zap, color: '#FF6B35' },
  commande: { Icon: ShoppingCart, color: '#2e7d32' },
  pronostic: { Icon: Target, color: '#FF6B35' },
};

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "a l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return 'hier';
  return `il y a ${Math.floor(diff / 86400)}j`;
}

export function NotificationsBloc({ notifs, onMarkAllRead, onReadOne }) {
  const unread = notifs.filter(n => !n.read).length;
  const [open, setOpen] = useState(unread > 0);
  const [touched, setTouched] = useState(false);
  useEffect(() => { if (!touched && unread > 0) setOpen(true); }, [unread, touched]);
  const today = new Date(Date.now() - 10 * 3600000).toISOString().slice(0, 10);
  const sorted = [...notifs].sort((a, b) => {
    const aToday = a.created_at?.startsWith(today) ? 1 : 0;
    const bToday = b.created_at?.startsWith(today) ? 1 : 0;
    if (aToday !== bToday) return bToday - aToday;
    if (a.read !== b.read) return a.read ? 1 : -1;
    return (b.created_at || '').localeCompare(a.created_at || '');
  }).slice(0, 10);

  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg overflow-hidden" data-testid="notifications-bloc">
      <button onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-white/5 ${open ? 'border-b border-[#2A2A2E]' : ''}`}
        data-testid="notifications-accordion-toggle" aria-expanded={open}>
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#FF6B35]" />
          <span className="text-xs font-bold text-white uppercase tracking-wider">Notifications</span>
          {unread > 0 && (
            <span className="bg-[#FF6B35] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{unread}</span>
          )}
          {!open && unread === 0 && notifs.length > 0 && (
            <span className="text-[10px] text-zinc-600">({notifs.length})</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {unread > 0 && open && (
            <span onClick={e => { e.stopPropagation(); onMarkAllRead(); }}
              className="text-[10px] text-[#FF6B35] font-semibold cursor-pointer" data-testid="mark-all-read">Tout lu</span>
          )}
          <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <div className={`transition-all duration-300 overflow-hidden ${open ? 'max-h-[400px]' : 'max-h-0'}`} data-testid="notifications-accordion-content">
        <div className="divide-y divide-[#2A2A2E]/50 max-h-[400px] overflow-y-auto">
          {sorted.length === 0 && (
            <div className="px-4 py-6 text-center">
              <p className="text-xs text-zinc-600">Aucune notification</p>
            </div>
          )}
          {sorted.map(n => {
            const cfg = NOTIF_TYPE[n.type] || { Icon: Info, color: '#A1A1AA' };
            const IconComp = cfg.Icon;
            return (
              <button key={n.id} onClick={() => onReadOne(n)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5 ${!n.read ? 'bg-[#FF6B35]/5' : ''}`}
                data-testid={`notif-item-${n.id}`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${cfg.color}15` }}>
                  <IconComp className="w-4 h-4" style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${!n.read ? 'text-white' : 'text-zinc-400'}`}>{n.title}</p>
                  <p className="text-[11px] text-zinc-500 leading-relaxed mt-0.5">{n.message}</p>
                </div>
                <div className="flex flex-col items-end flex-shrink-0 gap-1">
                  <span className="text-[9px] text-zinc-600">{formatTime(n.created_at)}</span>
                  {!n.read && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.color }} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
